import { useEffect, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Modal, Button, Banner, ProgressBar } from "../common";
import { sortAlpha } from "../../utils/alphaSort";
import { resendInvitationsBulk } from "../../services/invitationApi";

// [NEW] Resends the login link to every selected invitation. confirm -> sending (progress) -> summary.
// rows = invitation rows from InvitationsTable ({ employeeId, employeeName, email, ... }).
export default function BulkResendModal({ rows, onClose, onFinished }) {
  const [phase, setPhase] = useState("confirm"); // confirm | sending | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [outcome, setOutcome] = useState(null);

  const open = Array.isArray(rows) && rows.length > 0;
  useEffect(() => { if (open) { setPhase("confirm"); setOutcome(null); setProgress({ done: 0, total: rows.length }); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setPhase("sending");
    const res = await resendInvitationsBulk(rows, { onProgress: setProgress });
    setOutcome(res);
    setPhase("done");
    onFinished?.(res);
  };

  const count = (kind) => outcome?.results.filter((r) => r.outcome === kind).length || 0;
  const problems = outcome?.results.filter((r) => r.outcome !== "sent") || [];
  const busy = phase === "sending";
  const n = rows?.length || 0;

  return (
    <Modal
      open={open}
      size="md"
      title={phase === "done" ? "Resend finished" : `Resend ${n} invitation${n === 1 ? "" : "s"}`}
      subtitle={phase === "confirm" ? "Each person gets a new link by email. Their earlier links stop working. A new link is valid for 72 hours." : undefined}
      onClose={busy ? undefined : onClose}
      footer={
        phase === "confirm" ? <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button icon={FiRefreshCw} onClick={send}>Resend {n}</Button></>
          : phase === "sending" ? <Button loading disabled>Sending…</Button>
            : <Button onClick={onClose}>Close</Button>
      }
    >
      {phase === "confirm" && (
        <ul className="bulk-picker">
          {sortAlpha(rows, "employeeName").map((r) => <li key={r.id}><strong>{r.employeeName}</strong> <span className="muted">{r.email || "no email on record"} · {r.statusLabel}</span></li>)}
        </ul>
      )}
      {phase === "sending" && (
        <div className="form-stack">
          <p>Sending… {progress.done} of {progress.total}. Please keep this window open.</p>
          <ProgressBar value={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} />
        </div>
      )}
      {phase === "done" && outcome && (
        <div className="form-stack">
          <Banner tone={count("failed") ? "warning" : "success"} title={`${count("sent")} resent${count("skipped") ? `, ${count("skipped")} skipped` : ""}${count("failed") ? `, ${count("failed")} failed` : ""}`}>
            {count("sent") > 0 && "The emails go out within a minute. Track delivery in this list."}
          </Banner>
          {outcome.aborted && <Banner tone="danger" title="Stopped early">{outcome.aborted}</Banner>}
          {problems.length > 0 && (
            <ul className="bulk-picker">
              {sortAlpha(problems).map((r) => (
                <li key={r.id}><strong>{r.name}</strong> <span className={r.outcome === "failed" ? "form-error" : "muted"}>{r.outcome === "failed" ? "Failed: " : "Skipped: "}{r.message}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
